import { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { CaretRight, Plus } from "@phosphor-icons/react";
import { UnitPicker } from "@/components/UnitPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GST_RATES = [0, 5, 12, 18, 28];

// Tally-style product picker: click/typing filters a side "List of Items"
// panel. Ctrl+A opens an inline Create Product form (name, HSN, price,
// GST %, unit via UnitPicker); the saved product is auto-selected.
export const ProductPicker = ({ products, value, onChange, onProductCreated, testid = "product-picker" }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const [creating, setCreating] = useState(false);
  const [np, setNp] = useState({ name: "", hsn_sac: "", price: "", gst_rate: "18", unit: "PCS" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const selected = products.find((p) => p.id === value);
  const filtered = q
    ? products.filter((p) =>
        p.name.toLowerCase().includes(q.toLowerCase()) ||
        (p.hsn_sac || "").toLowerCase().includes(q.toLowerCase()))
    : products;

  const close = () => { setOpen(false); setQ(""); setCreating(false); setErr(""); };
  const pick = (p) => { onChange(p); close(); };

  const createProduct = async () => {
    if (!np.name.trim() || busy) return;
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/products`, {
        name: np.name.trim(),
        hsn_sac: np.hsn_sac.trim(),
        price: parseFloat(np.price) || 0,
        gst_rate: parseFloat(np.gst_rate) || 0,
        unit: (np.unit || "PCS").trim().toUpperCase(),
      });
      if (onProductCreated) onProductCreated(r.data);
      pick(r.data);
      setNp({ name: "", hsn_sac: "", price: "", gst_rate: "18", unit: "PCS" });
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed to create product");
    }
    setBusy(false);
  };

  const onKey = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
      e.preventDefault();
      setOpen(true);
      setCreating(true);
      setNp((f) => ({ ...f, name: q }));
      setErr("");
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setHi((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (open && !creating) {
        e.preventDefault();
        if (filtered[hi]) pick(filtered[hi]);
      }
    } else if (e.key === "Escape") {
      close();
    }
  };

  return (
    <div className="relative min-w-[180px] flex-1">
      <input
        data-testid={`${testid}-input`}
        value={open ? q : (selected?.name || "")}
        placeholder="Search items… (Ctrl+A: new)"
        onFocus={() => { setOpen(true); setQ(""); setHi(0); }}
        onChange={(e) => { setQ(e.target.value); setHi(0); if (!open) setOpen(true); }}
        onKeyDown={onKey}
        className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
        autoComplete="off"
      />
      {creating && (
        <div className="absolute left-0 top-full mt-1 z-40 w-80 p-3 bg-amber-50 border-2 border-amber-300 rounded-sm shadow-lg" data-testid={`${testid}-create-form`}>
          <p className="text-[11px] font-black uppercase tracking-wider text-amber-800 mb-1.5">Create New Product</p>
          <input value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createProduct(); } else if (e.key === "Escape") { e.stopPropagation(); setCreating(false); } }}
            placeholder="Product name *" autoFocus data-testid={`${testid}-create-name`} data-enter-nav-skip="true"
            className="w-full px-2.5 py-1.5 border border-amber-300 rounded-sm text-sm mb-2" />
          <div className="flex gap-2 mb-2">
            <input value={np.hsn_sac} onChange={(e) => setNp({ ...np, hsn_sac: e.target.value })}
              placeholder="HSN/SAC" data-testid={`${testid}-create-hsn`} data-enter-nav-skip="true"
              className="w-24 px-2.5 py-1.5 border border-amber-300 rounded-sm text-sm" />
            <input type="number" step="0.01" value={np.price} onChange={(e) => setNp({ ...np, price: e.target.value })}
              placeholder="Price ₹" data-testid={`${testid}-create-price`} data-enter-nav-skip="true"
              className="flex-1 px-2.5 py-1.5 border border-amber-300 rounded-sm text-sm" />
            <select value={np.gst_rate} onChange={(e) => setNp({ ...np, gst_rate: e.target.value })}
              data-testid={`${testid}-create-gst`}
              className="w-20 px-1 py-1.5 border border-amber-300 rounded-sm text-sm bg-white">
              {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
            </select>
          </div>
          <div className="mb-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1">Unit</p>
            <UnitPicker value={np.unit} onChange={(abbr) => setNp((f) => ({ ...f, unit: abbr }))} testid={`${testid}-create-unit`} />
          </div>
          {err && <p className="text-[11px] font-bold text-red-600 mb-2" data-testid={`${testid}-create-error`}>{err}</p>}
          <div className="flex gap-2">
            <button type="button" onClick={createProduct} disabled={busy || !np.name.trim()}
              data-testid={`${testid}-create-save`}
              className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-sm text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1">
              <Plus size={12} weight="bold" /> {busy ? "Saving…" : "Save Product"}
            </button>
            <button type="button" onClick={() => { setCreating(false); setErr(""); }}
              data-testid={`${testid}-create-cancel`}
              className="px-3 py-1.5 border border-slate-300 rounded-sm text-xs font-bold text-slate-600 hover:bg-slate-100">
              Cancel
            </button>
          </div>
        </div>
      )}
      {open && createPortal(
        <div
          className="fixed top-1/2 -translate-y-1/2 right-6 h-[80vh] w-80 bg-white border-2 border-blue-400 rounded-sm shadow-2xl z-[120] flex flex-col pointer-events-auto"
          data-group-picker-panel="true"
          data-testid={`${testid}-panel`}
        >
          <div className="px-4 py-2.5 bg-blue-900 text-white text-xs font-black uppercase tracking-widest text-center flex-shrink-0">
            List of Items
          </div>
          {q && !creating && (
            <div className="px-4 py-1.5 bg-blue-50 border-b border-blue-100 text-[11px] font-bold text-blue-800 flex-shrink-0" data-testid={`${testid}-filter-hint`}>
              "{q}" — {filtered.length} match{filtered.length === 1 ? "" : "es"}
            </div>
          )}
          <div className="flex-1 overflow-y-auto bg-blue-50/40">
            {filtered.length === 0 && !creating && (
              <p className="text-xs text-slate-400 text-center py-6">No matching item — press Ctrl+A to create one</p>
            )}
            {filtered.map((p, i) => (
              <button
                key={p.id}
                type="button"
                ref={i === hi ? (el) => el && el.scrollIntoView({ block: "nearest" }) : undefined}
                onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                onMouseEnter={() => setHi(i)}
                data-testid={`${testid}-option-${p.id}`}
                className={`w-full text-left px-4 py-1.5 flex items-center gap-1.5 ${
                  i === hi ? "bg-blue-600 text-white font-bold"
                    : p.id === value ? "bg-blue-100 text-blue-900 font-semibold"
                      : "text-slate-800 hover:bg-blue-100"
                }`}
              >
                {i === hi && <CaretRight size={11} weight="bold" />}
                <span className="flex-1 text-sm truncate">{p.name}</span>
                <span className={`text-[10px] font-black px-1 rounded-sm ${i === hi ? "bg-white/20 text-white" : "bg-blue-100 text-blue-700"}`}>{p.gst_rate ?? 0}%</span>
                <span className={`text-[10px] font-bold ${i === hi ? "text-blue-100" : "text-slate-500"}`}>₹{p.price} · {p.unit || "PCS"}</span>
              </button>
            ))}
          </div>
          <div className="px-4 py-1.5 bg-slate-50 border-t border-slate-200 text-[10px] font-semibold text-slate-500 text-center flex-shrink-0">
            ↑↓ Navigate · Enter Select · Ctrl+A New Product
          </div>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); close(); }} data-testid={`${testid}-close`}
            className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-900 hover:bg-slate-100 border-t border-slate-200 flex-shrink-0">
            Close (Esc)
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};
